import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  InputAdornment,
  Grid,
  Autocomplete,
  TextField,
  Collapse
} from '@mui/material';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { useDispatch } from 'react-redux';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import { getBankDropdownData } from '../../store/Slice/Global/GlobalSlice';
import { pillTabButtonSx } from '../Global/Styling';

export default function SalesToolbar({
  parent,
  selectStatus,
  setSelectStatus,
  handleDownloadMatchDetails,
  setDownloadPopup,
  downloadPopup,
  filterName,
  onFilterName,
  filterParameter,
  setFilterParameter
}) {
  const [isOpenFilter, setOpenFilter] = useState(false);
  const dispatch = useDispatch();

  const { LoginData } = useLoginSelector();
  const { MasterTypeListDrop, BankDrop } = useGlobalSelector();

  const handleRemoveFilter = () => {
    setSelectStatus(' ');
    setFilterParameter((pre) => ({
      ...pre,
      bankId: null
    }));
  };

  const handleStatusChange = (statusValue) => {
    setSelectStatus(statusValue);
  };

  const getBankDropApi = useCallback(() => {
    if (!LoginData) return;
    const data = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getBankDropdownData(data));
  }, [LoginData, dispatch]);

  useEffect(() => {
    getBankDropApi();
  }, [getBankDropApi]);

  const statusTabs =
    MasterTypeListDrop && MasterTypeListDrop.length > 0 ? MasterTypeListDrop.slice().reverse() : [];

  return (
    <Box sx={{ width: '100%', bgcolor: '#ffffff' }}>
      {/* Top Bar: Tabs on Left + Search, Download & Filter on Right */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        {/* Left Status Tabs */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.25,
            overflowX: 'auto',
            p: '2px 2px 6px',
            '::-webkit-scrollbar': { display: 'none' }
          }}
        >
          {statusTabs.map((tab) => {
            const isActive = selectStatus === tab.value;
            return (
              <Box
                key={tab.value}
                component="button"
                onClick={() => handleStatusChange(tab.value)}
                sx={pillTabButtonSx(isActive)}
              >
                {tab.label}
              </Box>
            );
          })}
        </Box>

        {/* Right Action Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          {/* Download CSV Report */}
          <Tooltip
            title={`Download ${selectStatus === 260 ? 'Mis matched' : 'Matched'} Report`}
            placement="top"
            arrow
          >
            <IconButton
              onClick={() => setDownloadPopup(true)}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '8px',
                border: '1.5px solid #bae6fd',
                bgcolor: '#f0f9ff',
                color: '#0284c7',
                '&:hover': {
                  bgcolor: '#e0f2fe',
                  borderColor: '#7dd3fc'
                }
              }}
            >
              <CloudDownloadOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {/* Search Box */}
          <TextField
            size="small"
            value={filterName}
            onChange={onFilterName}
            placeholder="Search Sales..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: { xs: '100%', sm: 260, md: 290 },
              '& .MuiOutlinedInput-root': {
                bgcolor: '#ffffff',
                borderRadius: '10px',
                fontSize: '13px',
                '& fieldset': {
                  borderColor: '#e2e8f0'
                },
                '&:hover fieldset': {
                  borderColor: '#cbd5e1'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0284c7'
                }
              }
            }}
          />

          {/* Filter Toggle Button */}
          <Button
            variant="outlined"
            onClick={() => setOpenFilter((prev) => !prev)}
            startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: isOpenFilter ? '#0284c7' : '#475569',
              borderColor: isOpenFilter ? '#0284c7' : '#e2e8f0',
              bgcolor: isOpenFilter ? 'rgba(2, 132, 199, 0.06)' : '#ffffff',
              height: 40,
              px: 2,
              '&:hover': {
                borderColor: '#0284c7',
                bgcolor: 'rgba(2, 132, 199, 0.08)'
              }
            }}
          >
            Filters
          </Button>
        </Box>
      </Box>

      {/* Collapsible Filter Panel */}
      <Collapse in={isOpenFilter}>
        <Box
          sx={{
            p: 2.5,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                id="combo-box-bank"
                options={BankDrop || []}
                size="small"
                getOptionLabel={(option) => option.label || ''}
                renderOption={(props, option) => <li {...props}>{option.label}</li>}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder="Select Bank"
                    sx={{
                      bgcolor: '#ffffff',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: 13,
                        '& fieldset': { borderColor: '#e2e8f0' }
                      }
                    }}
                  />
                )}
                value={BankDrop?.find((item) => item.value === filterParameter?.bankId) || null}
                onChange={(e, newValue) => {
                  setFilterParameter((pre) => ({
                    ...pre,
                    bankId: newValue?.value || null
                  }));
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Tooltip title="Reset Filter">
                <IconButton
                  onClick={handleRemoveFilter}
                  size="small"
                  sx={{
                    bgcolor: '#fee2e2',
                    color: '#dc2626',
                    borderRadius: '8px',
                    p: 1,
                    '&:hover': { bgcolor: '#fecaca' }
                  }}
                >
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      {/* Download Confirmation Dialog */}
      <Dialog
        open={downloadPopup}
        onClose={() => setDownloadPopup(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            fontWeight: 700,
            fontSize: 16,
            color: '#1e293b',
            py: 2,
            px: 3
          }}
        >
          Download Report
          <IconButton
            size="small"
            onClick={() => setDownloadPopup(false)}
            sx={{ color: '#64748b' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#475569', fontSize: 14 }}>
            Are you sure you want to download the{' '}
            <b style={{ color: '#0284c7' }}>{selectStatus === 260 ? 'Mis matched' : 'Matched'}</b>{' '}
            sales report?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setDownloadPopup(false)}
            sx={{
              borderRadius: '8px',
              borderColor: '#cbd5e1',
              color: '#475569',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { borderColor: '#94a3b8', bgcolor: '#f1f5f9' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownloadMatchDetails}
            variant="contained"
            sx={{
              borderRadius: '8px',
              bgcolor: '#0284c7',
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#0369a1' }
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
